import { action, makeObservable } from 'mobx';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import {
  EmailCodeStore,
  FlowStart,
  isInvalidChallenge,
} from './emailCodeStore';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import { LOGIN_PAGE_CONTEXT } from '../utils/constants';

type LoginPayload = Parameters<RootStore['navigationStore']['postLogin']>[0];

/**
 * Logging in with a code sent to the user's email instead of a password. The
 * login pauses on REQUIRE_LOGIN_CHALLENGE until the code is verified.
 *
 * The challenge is the login store's `loginFlow`: a code request continues a
 * login waiting there for its first factor, and "Use Your Password" sends
 * the password with the challenge, so both finish the same login.
 */
export class LoginEmailFactorStore extends EmailCodeStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable<this, 'finish' | 'continueWithMfa'>(this, {
      finish: action,
      continueWithMfa: action,
      start: action.bound,
      backToEmail: action.bound,
      switchToPassword: action.bound,
    });
  }

  protected get challenge(): string | undefined {
    return this.rootStore.loginStore.loginFlow.tokenAt(
      this.email,
      StateType.REQUIRE_LOGIN_CHALLENGE,
    );
  }

  protected set challenge(token: string | undefined) {
    const { loginStore } = this.rootStore;
    if (token) {
      loginStore.loginFlow.record(
        this.email,
        token,
        StateType.REQUIRE_LOGIN_CHALLENGE,
      );
    } else if (this.challenge) {
      // Only an open challenge is dropped: a login still waiting for its
      // first factor stays for the password.
      loginStore.endLoginFlow();
    }
  }

  enableAccess() {
    return (
      Boolean(this.email) &&
      this.rootStore.loginStore.isLoginWithEmailCodeEnabled
    );
  }

  /**
   * Whether a code was already sent to this email and can still be entered:
   * its challenge is kept (not reset, not for another email) and login by code
   * is still on. If so, the code screen can open again on that code without
   * sending a new one.
   */
  hasPendingCodeFor(email: string) {
    return (
      this.email === email && Boolean(this.challenge) && this.enableAccess()
    );
  }

  /**
   * Sends the first code and opens the code screen. Returns the error code
   * when nothing was sent, so the caller can show it on the login form.
   */
  async start(email: string): Promise<string | undefined> {
    if (this.isLoading) {
      return undefined;
    }
    if (this.email !== email) {
      this.reset();
      this.email = email;
    }
    if (!this.challenge) {
      const version = this.requestVersion;
      const sendError = await this.resendCode({ lockOnFailure: false });
      if (this.isStale(version)) {
        // Abandoned meanwhile (cleared, or another email): nothing to show.
        return undefined;
      }
      if (sendError) {
        // Nothing was sent, so do not open a screen that claims otherwise.
        return sendError;
      }
      if (this.isCompleting) {
        return undefined;
      }
    }
    this.rootStore.navigationStore.navigate(ROUTES.LOGIN_EMAIL_CODE);
    return undefined;
  }

  /** Starts a login with factor EMAIL, continuing one waiting for its first factor. */
  protected async startFlow(version: number): Promise<FlowStart> {
    const { loginStore } = this.rootStore;
    const firstFactor = loginStore.loginFlow.tokenAt(
      this.email,
      StateType.REQUIRE_FIRST_FACTOR,
    );
    try {
      return await loginStore.requestEmailCode(this.email, firstFactor);
    } catch (error) {
      if (firstFactor && isInvalidChallenge(error) && !this.isStale(version)) {
        loginStore.endLoginFlow();
      }
      throw error;
    }
  }

  /** The account also requires a second factor, after the code or without one. */
  protected continueWithMfa(payload: any) {
    this.rootStore.loginStore.endLoginFlow();
    this.reset();
    this.rootStore.twoFactorAuthStore.startChallenge(payload, true);
  }

  protected finish(payload: LoginPayload) {
    this.rootStore.loginStore.endLoginFlow();
    this.isCompleting = true;
    this.rootStore.loginStore.completeEmailLogin(this.email, payload);
  }

  /**
   * On the code screen: the account has no password to fall back on. Solving
   * the CAPTCHA sends the code with its token.
   */
  protected askForCaptcha(errorCode: string) {
    const { loginStore, navigationStore } = this.rootStore;
    loginStore.pendingCaptcha.request(errorCode, LOGIN_PAGE_CONTEXT, () =>
      this.resendCode(),
    );
    if (navigationStore.currentRoute !== ROUTES.LOGIN_EMAIL_CODE) {
      navigationStore.navigate(ROUTES.LOGIN_EMAIL_CODE);
    }
  }

  /** Keeps the challenge, so switching back to the code does not resend. */
  switchToPassword() {
    if (this.isLoading) {
      return;
    }
    const { loginStore, userDataStore, navigationStore } = this.rootStore;
    // Already on the login form (a send from it was refused by CAPTCHA): keep
    // what the user typed there.
    if (navigationStore.currentRoute !== ROUTES.LOGIN_PASSWORD) {
      userDataStore.setEmail(this.email);
      loginStore.onInitFields();
      loginStore.navigateToLoginForm();
    }
  }

  /**
   * Change email: back to the email step, as the login form's clear button
   * does. The email step clears its field on mount, so it opens empty.
   */
  backToEmail() {
    this.reset();
    this.rootStore.loginStore.clearEmail();
  }
}
